// @ts-check
// ============================================================
// Area trends (F6 + F-b) — serie storica dei conteggi area.
//
// Sorgente PRIMARIA: tabella `kpi_history` (un record per giorno, scritto
// dall'app). È affidabile e indipendente dalla cadenza dei backup.
// Sorgente di FALLBACK: snapshot di `backups.row_counts` (comportamento storico),
// usata finché `kpi_history` è vuota o non ancora creata sul DB.
//
// `recordKpiSnapshot()` registra il conteggio di oggi (solo admin), così la
// serie cresce di un punto al giorno senza tabelle o cron aggiuntivi.
// ============================================================

/** @type {{points: Array<{date:string,clienti:number,accessi:number,contratti:number,scadenze:number,risorse:number}>, source?:string}|null} */
let _trendsCache = null;
/** @type {string|null} giorno (YYYY-MM-DD) per cui lo snapshot è già stato tentato in questa sessione */
let _snapshotTriedFor = null;

/**
 * @param {boolean} [force] ricarica ignorando la cache.
 */
async function loadAreaTrends(force) {
  if (_trendsCache && !force) return _trendsCache;

  // 1) Sorgente primaria: storico dedicato kpi_history.
  try {
    // @ts-ignore — client globale (supabase-client.js)
    const sb = globalThis.sb;
    if (sb) {
      const { data, error } = await sb
        .from('kpi_history')
        .select('giorno,clienti,accessi,contratti,scadenze,risorse')
        .order('giorno', { ascending: true });
      // Serve almeno 2 giorni per una linea di trend: finché kpi_history è più
      // povera, restiamo sui backup che possono avere più storico pregresso.
      if (!error && Array.isArray(data) && data.length >= 2) {
        const points = data.map((r) => ({
          date: String(r.giorno).slice(0, 10),
          clienti: Number(r.clienti || 0),
          accessi: Number(r.accessi || 0),
          contratti: Number(r.contratti || 0),
          scadenze: Number(r.scadenze || 0),
          risorse: Number(r.risorse || 0)
        }));
        _trendsCache = { points, source: 'kpi_history' };
        return _trendsCache;
      }
    }
  } catch (e) {
    console.warn('[area-trends] kpi_history non disponibile, uso i backup:', e);
  }

  // 2) Fallback: deriva dagli snapshot di backup (un punto per giorno).
  try {
    // @ts-ignore — backupApi globale (data/backups.js)
    const list = await backupApi.list();
    /** @type {Map<string, any>} */
    const byDay = new Map();
    // list è ordinata desc per data: invertiamo così l'ultimo write vince
    (list || [])
      .slice()
      .reverse()
      .forEach((b) => {
        const rc = b && b.row_counts;
        const day = (b && b.created_at ? String(b.created_at) : '').slice(0, 10);
        if (!rc || !day) return;
        byDay.set(day, {
          date: day,
          clienti: Number(rc.clienti || 0),
          accessi: Number(rc.accessi || 0),
          contratti: Number(rc.contratti || 0),
          scadenze: Number(rc.scadenze || 0),
          risorse: Number(rc.risorse || 0)
        });
      });
    const points = [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
    _trendsCache = { points, source: 'backups' };
    return _trendsCache;
  } catch (e) {
    console.warn('[area-trends] non disponibile:', e);
    return { points: [] };
  }
}

/**
 * Registra (upsert) il conteggio di oggi in kpi_history. Best-effort:
 * - solo admin (RLS lo impone comunque);
 * - al massimo un tentativo per giorno/sessione;
 * - se la tabella non esiste ancora, fallisce in silenzio e i trend
 *   continuano a usare i backup.
 */
async function recordKpiSnapshot() {
  try {
    // @ts-ignore — globali
    const sb = globalThis.sb;
    // @ts-ignore
    const st = globalThis.state;
    if (!sb || !st || !Array.isArray(st.clients)) return;
    // @ts-ignore — solo admin scrive lo snapshot
    if (typeof globalThis.isAdmin === 'function' && !globalThis.isAdmin()) return;

    const today = new Date().toISOString().slice(0, 10);
    if (_snapshotTriedFor === today) return;
    _snapshotTriedFor = today; // ottimistico: un solo tentativo per giorno/sessione

    let accessi = 0;
    let contratti = 0;
    let scadenze = 0;
    st.clients.forEach((c) => {
      accessi += Object.keys(c.accessi || {}).length;
      if (c.contratto && (c.contratto.numero || c.contratto.fine)) contratti++;
      scadenze += (c.scadenze || []).length;
    });

    const row = {
      giorno: today,
      clienti: st.clients.length,
      accessi,
      contratti,
      scadenze,
      risorse: (st.resources || []).length
    };

    const { error } = await sb.from('kpi_history').upsert(row, { onConflict: 'giorno' });
    if (!error) invalidateAreaTrends();
  } catch (e) {
    // silenzioso: la dashboard non deve mai rompersi per questo
  }
}

/** Variazione tra primo e ultimo punto per una metrica. */
function trendDelta(points, key) {
  if (!points || points.length < 2) return 0;
  return Number(points[points.length - 1][key] || 0) - Number(points[0][key] || 0);
}

function invalidateAreaTrends() {
  _trendsCache = null;
}

Object.assign(globalThis, { loadAreaTrends, trendDelta, invalidateAreaTrends, recordKpiSnapshot });
export {};
